import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {Client} from '../client-form/client.model';
import {
    ICC,
    Instrument,
    InstrumentBrand,
    InstrumentModel,
    InstrumentType,
    Magnitude,
    MeasurePoint, StandardValue
} from '../../interfaces/interfaces';
import {Project} from '../../pages/product/product.model';
import {tap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class InstrumentService {

    private _allInstrumentType: BehaviorSubject<InstrumentType[]> = new BehaviorSubject<InstrumentType[]>(null);
    public allInstrumentType: InstrumentType[];
    public allInstrumentTypeMap: Map<number, InstrumentType> = new Map<number, InstrumentType>();
    private _allInstrumentBrands: BehaviorSubject<InstrumentBrand[]> = new BehaviorSubject<InstrumentBrand[]>(null);
    private allInstrumentBrands: InstrumentBrand[];
    private allInstrumentModels: InstrumentModel[];
    private _allInstrumentModels: BehaviorSubject<InstrumentModel[]> = new BehaviorSubject<InstrumentModel[]>(null);
    private allStandards: StandardValue[];
    private _allStandards: BehaviorSubject<StandardValue[]> = new BehaviorSubject<StandardValue[]>(null);
    private allIcc: ICC[];
    private _allIcc: BehaviorSubject<ICC[]> = new BehaviorSubject<ICC[]>(null);
    private baseUrl = '/ypm/api';


    constructor(private _httpClient: HttpClient) {
        this.getAllInstrumentType().subscribe();
        this.getAllInstrumentBrands().subscribe();
        this.getAllInstrumentModels().subscribe();
        this.getAllStandards().subscribe();
        this.getAllICC().subscribe();
    }

    get allInstrumentType$(): Observable<InstrumentType[]> {
        return this._allInstrumentType.asObservable();
    }

    get allInstrumentBrands$(): Observable<InstrumentBrand[]> {
        return this._allInstrumentBrands.asObservable();
    }

    get allInstrumentModels$(): Observable<InstrumentModel[]> {
        return this._allInstrumentModels.asObservable();
    }

    get allIcc$(): Observable<ICC[]> {
        return this._allIcc.asObservable();
    }

    getInstrument(id: number): Observable<Instrument> {
        return this._httpClient.get<Instrument>('/ypm/api/instrument/one/' + id);
    }

    getAllInstrumentType(): Observable<InstrumentType[]> {
        return this._httpClient.get<InstrumentType[]>('/ypm/api/instrument/allFamilies').pipe(
            tap(resp => {
                this.allInstrumentType = resp;
                this._allInstrumentType.next(resp);
                resp.forEach(inst => {
                    this.allInstrumentTypeMap.set(inst.id, inst);
                });
            }
        ));
    }

    getAllICC(): Observable<ICC[]> {
        return this._httpClient.get<ICC[]>('/ypm/api/instrument/allIcc').pipe(
            tap(resp => {
                this.allIcc = resp;
                this._allIcc.next(resp);
            })
        );
    }

    getAllStandards(): Observable<StandardValue[]> {
        return this._httpClient.get<StandardValue[]>('/ypm/api/instrument/allStandards').pipe(
            tap(resp => {
                 this.allStandards = resp;
                 this._allStandards.next(resp);
            })
        );
    }

    get allStandards$(): Observable<StandardValue[]> {
        return this._allStandards.asObservable();
    }

    getAllStandardsByModel(magnitudeName: string): StandardValue[] {
        return  this.allStandards.filter(standard => standard.magnitudeName.trim() === magnitudeName.trim());
    }

    getAllInstrumentBrands(): Observable<InstrumentBrand[]> {
        return this._httpClient.get<InstrumentBrand[]>('/ypm/api/instrument/allBrands').pipe(
            tap(resp => {
                this.allInstrumentBrands = resp;
                this._allInstrumentBrands.next(resp);
            })
        );
    }

    getProject(projectNumber: number): Observable<Project> {
        return this._httpClient.get<Project>('/ypm/api/project/projectNumber/' + projectNumber);
    }

    getProjectBySerieOrCode(projectNumber: number): Observable<Project> {
        return this._httpClient.get<Project>('/ypm/api/instrument/serieOrCode/' + projectNumber);
    }

    public getAllInstrumentModels(): Observable<InstrumentModel[]> {
        return this._httpClient.get<InstrumentModel[]>('/ypm/api/instrument/allModels').pipe(
            tap(resp => {
                this.allInstrumentModels = resp;
                this._allInstrumentModels.next(resp);
            })
        );
    }

    saveInstrumentType(theInstrumentType: InstrumentType): Observable<InstrumentType> {
        return this._httpClient.post<InstrumentType>('/ypm/api/instrument/instrumentType', theInstrumentType).pipe(
            tap(resp => {
                // reempazar el nuevo instrumento de la lista
                const index = this.allInstrumentType.findIndex(inst => inst.id === theInstrumentType.id);
                if (index !== -1) {
                    this.allInstrumentType[index] = theInstrumentType;
                    this._allInstrumentType.next(this.allInstrumentType);
                    this.allInstrumentTypeMap.set(theInstrumentType.id, theInstrumentType);
                }else{
                    this.allInstrumentType.push(theInstrumentType);
                    this._allInstrumentType.next(this.allInstrumentType);
                    this.allInstrumentTypeMap.set(theInstrumentType.id, theInstrumentType);
                }
            })
        );
    }

    saveInstrumentBrand(theInstrumentBrand: InstrumentBrand): Observable<InstrumentBrand> {
        return this._httpClient.post<InstrumentBrand>('/ypm/api/instrument/saveBrand', theInstrumentBrand).pipe(
            tap(resp => {
                // reempazar el nuevo instrumento de la lista
                const index = this.allInstrumentBrands.findIndex(inst => inst.id === theInstrumentBrand.id);
                if (index !== -1) {
                    this.allInstrumentBrands[index] = theInstrumentBrand;
                    this._allInstrumentBrands.next(this.allInstrumentBrands);
                }else{
                    this.allInstrumentBrands.push(theInstrumentBrand);
                    this._allInstrumentBrands.next(this.allInstrumentBrands);
                }
            })
        );
    }

    deleteInstrumentType(id: number): Observable<void> {
        return this._httpClient.delete<void>('/ypm/api/instrument/instrumentType/' + id).pipe(
            tap(() => {
                // eliminar el instrumento de la lista
                const index = this.allInstrumentType.findIndex(inst => inst.id === id);
                if (index !== -1) {
                    this.allInstrumentType.splice(index, 1);
                    this._allInstrumentType.next(this.allInstrumentType);
                    this.allInstrumentTypeMap.delete(id);
                }
            })
        );
    }

    deleteInstrumentBrand(id: number): Observable<void> {
        return this._httpClient.delete<void>('/ypm/api/instrument/instrumentBrand/' + id).pipe(
            tap(() => {
                // eliminar el instrumento de la lista
                const index = this.allInstrumentBrands.findIndex(inst => inst.id === id);
                if (index !== -1) {
                    this.allInstrumentBrands.splice(index, 1);
                    this._allInstrumentBrands.next(this.allInstrumentBrands);
                }
            })
        );
    }

    getInstrumentModels(brandId: number): Observable<InstrumentModel[]> {
        return this._httpClient.get<InstrumentModel[]>('/ypm/api/instrument/allModelsByBrand/' + brandId);
    }

    getInstrumentType(typeId: number): InstrumentType {
        return this.allInstrumentType.find(inst => inst.id === typeId);
    }

    updateMeasurePoint(magnitudId: number, fila: MeasurePoint): Observable<MeasurePoint> {
        return this._httpClient.put<MeasurePoint>('/ypm/api/instrument/updateMeasurePoint/' + magnitudId, fila);
    }

    deleteMeasurePoint(filaId: number): Observable<void> {
        return this._httpClient.delete<void>('/ypm/api/instrument/deleteMeasurePoint/' + filaId);
    }

    addOrUpdateMagnitude(id: number, magnitud: Magnitude): Observable<Magnitude> {
        return this._httpClient.post<Magnitude>('/ypm/api/instrument/addMagnitude/' + id, magnitud);
    }


    getModel(id): Observable<InstrumentModel> {
        return this._httpClient.get<InstrumentModel>('/ypm/api/instrument/model/' + id);
    }

    updateInstrumentModel(updatedModel: InstrumentModel): Observable<InstrumentModel> {
        return this._httpClient.put<InstrumentModel>('/ypm/api/instrument/updateModel', updatedModel).pipe(
            tap(resp => {
                // reempazar el nuevo instrumento de la lista
                const index = this.allInstrumentModels.findIndex(inst => inst.id === updatedModel.id);
                if (index >= 0) {
                    this.allInstrumentModels[index] = updatedModel;
                    this._allInstrumentModels.next(this.allInstrumentModels);
                }else{
                    this.allInstrumentModels.push(updatedModel);
                    this._allInstrumentModels.next(this.allInstrumentModels);
                }
            })
        );
    }

    deleteICC(id: number): Observable<void> {
        return this._httpClient.delete<void>('/ypm/api/instrument/deleteICC/' + id).pipe(
            tap(() => {
                // eliminar el ICC de la lista
                const index = this.allIcc.findIndex(icc => icc.id === id);
                if (index !== -1) {
                    this.allIcc.splice(index, 1);
                    this._allIcc.next(this.allIcc);
                }
            })
        );
    }

    saveICC(iccToSave: ICC): Observable<ICC> {
        return this._httpClient.post<ICC>('/ypm/api/instrument/saveIcc', iccToSave).pipe(
            tap(resp => {
                // reempazar el nuevo ICC de la lista
                const index = this.allIcc.findIndex(icc => icc.id === iccToSave.id);
                if (index !== -1) {
                    this.allIcc[index] = resp;
                    this._allIcc.next(this.allIcc);
                }else{
                    this.allIcc.push(resp);
                    this._allIcc.next(this.allIcc);
                }
            })
        );
    }

    getAllModelsByICC(iccId: number): InstrumentModel[] {
        const models = this._allInstrumentModels.getValue();
        return (models || []).filter(model => this.allInstrumentTypeMap.has(model.family?.id) &&
            this.allInstrumentTypeMap.get(model.family.id).icc.id === iccId);

    }

    importMagnitudes(from: number, to: number): Observable<Magnitude[]> {
        // Importar magnitudes al modelo actual
        return this._httpClient.post<Magnitude[]>(
            `/ypm/api/instrument/importMagnitudes/${from}/${to}`,
            {}
        );
    }

    getMagnitudes(modelId: number): Observable<Magnitude[]> {
        return this._httpClient.get<Magnitude[]>(`/ypm/api/instrument/magnitudes/${modelId}`).pipe(
            tap(magnitudes => {

            })
        );
    }

    deleteMagnitude(id: number): Observable<void> {
        return this._httpClient.delete<void>(`/ypm/api/instrument/deleteMagnitude/${id}`).pipe(
            tap(() => {
                // eliminar la magnitud de la lista
                const magnitudes = this._allInstrumentModels.getValue().find(model => model.id === id)?.magnitudes || [];
                const index = magnitudes.findIndex(mag => mag.id === id);
                if (index !== -1) {
                    magnitudes.splice(index, 1);
                    this._allInstrumentModels.next(this._allInstrumentModels.getValue());
                }
            })
        );

    }

    // createNewInstrumentModel(instrumentModel: InstrumentModel): Observable<InstrumentModel> {
    //     return this._httpClient.post<InstrumentModel>('/ypm/api/instrument/createNewModel', instrumentModel);
    // }
    getAllModelsByICCWithMagnitudes(iccId: number): Observable<InstrumentModel[]> {
        return this._httpClient.get<InstrumentModel[]>(`/ypm/api/instrument/allModelsWithMagnitudeByIcc/${iccId}`);
    }

    // updateInstrumentType(instrumentType: InstrumentType): Observable<InstrumentType> {
    //     // Si instrumentType1.id es 0 o falsy, tratamos como creación; si no, como actualización.
    //
    //     const url = `${this.baseUrl}/instrument/instrumentType`;
    //
    //     return this._httpClient.post<InstrumentType>(url, instrumentType);
    //   }
    generateReport(selectedService: Project): Observable<void> {
        return this._httpClient.post<void>('/ypm/api/instrument/generateReportByProject', selectedService);
    }

    createOrUpdateBrand(brand: InstrumentBrand): Observable<InstrumentBrand>{
        return this._httpClient.post<InstrumentBrand>('/ypm/api/instrument/saveBrand', brand).pipe(
            tap(resp => {
                // reempazar el nuevo instrumento de la lista
                const index = this.allInstrumentBrands.findIndex(inst => inst.id === brand.id);
                if (index !== -1) {
                    this.allInstrumentBrands[index] = brand;
                    this._allInstrumentBrands.next(this.allInstrumentBrands);
                }else{
                    this.allInstrumentBrands.push(brand);
                    this._allInstrumentBrands.next(this.allInstrumentBrands);
                }
            })
        );
    }

    getDocumentReportId(serviceId: number): Observable<number> {
        return this._httpClient.get<number>(`/ypm/api/instrument/getDocumentReportId/${serviceId}`);
    }
}
